import {
  ResourceDetailsDto,
  ResourceTagDto,
} from '@act/shared/data-transfer-objects';
import {
  EntityId,
  PayloadAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { addToEffectsMiddleware } from 'redux/utils/effects-middleware';
import { resourceRequests } from '../requests/resources.requests';
import { createApiThunk } from '../utils/api-thunk';
import { selectAllResourceTags } from './resourceTagsSlice';
import { getMemberInfo } from './memberSlice';
import { resetRedux } from '../actions/shared-actions';

const LOCAL_STORAGE_ZIPCODE_KEY = 'resource-hub-zipcode';

// THUNKS/ACTIONS ==============================================================
export const getResources = createApiThunk(resourceRequests.getResources);
export const getSelectedResource = createApiThunk(resourceRequests.getResource);

// INITIAL STATE ===============================================================
const resourcesAdapter = createEntityAdapter<ResourceDetailsDto, EntityId>({
  selectId: resource => resource.findHelpId,
});
const initialState = resourcesAdapter.getInitialState({
  zipcode: localStorage.getItem(LOCAL_STORAGE_ZIPCODE_KEY) || null,
  terms: null as string,
  tags: null as string[],
  clientId: null as number,
  loadingResources: false,
  loadingResource: false,
  selectedResourceId: null as number,
  selectedResource: null as ResourceDetailsDto,
});

// SLICE =======================================================================
export const resourcesSlice = createSlice({
  name: 'resources',
  initialState,
  reducers: {
    resourceZipcodeUpdated: (
      state,
      action: PayloadAction<{ zipcode: string }>
    ) => {
      state.zipcode = action.payload.zipcode;
    },
    resourceTermsUpdated: (state, action: PayloadAction<{ terms: string }>) => {
      state.terms = action.payload.terms;
      state.tags = null; // Tags and Terms should be mutually exclusive
    },
    resourceTagsUpdated: (state, action: PayloadAction<{ tags: string[] }>) => {
      state.tags = action.payload.tags;
      state.terms = null; // Tags and Terms should be mutually exclusive
    },
    clearResources: state => {
      state.selectedResource = null;
      state.selectedResourceId = null;
      return resourcesAdapter.removeAll(state);
    },
    resourceSelected: (
      state,
      action: PayloadAction<{ resourceId: number }>
    ) => {
      state.selectedResourceId = action.payload.resourceId;
      state.selectedResource = null;
      if (state.entities[action.payload.resourceId]) {
        state.selectedResource = state.entities[action.payload.resourceId];
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getResources.pending, state => {
        state.loadingResources = true;
        return resourcesAdapter.removeAll(state);
      })
      .addCase(getResources.fulfilled, (state, action) => {
        state.loadingResources = false;
        return resourcesAdapter.addMany(state, action.payload.programs);
      })
      .addCase(getResources.rejected, state => {
        state.loadingResources = false;
      })

      // Get selected resource
      .addCase(getSelectedResource.pending, state => {
        state.loadingResource = true;
      })
      .addCase(getSelectedResource.fulfilled, (state, action) => {
        state.loadingResource = false;
        state.selectedResource = action.payload;
      })
      .addCase(getSelectedResource.rejected, state => {
        state.loadingResource = false;
      })
      .addCase(getMemberInfo.fulfilled, (state, action) => {
        state.clientId = action.payload?.clientId;
      });
  },
});

// REDUCER =====================================================================
export const resourcesReducer = resourcesSlice.reducer;

// EXPORT ACTIONS HELPER v1.0 ==================================================
export const {
  resourceSelected,
  resourceZipcodeUpdated,
  resourceTermsUpdated,
  resourceTagsUpdated,
  clearResources,
} = resourcesSlice.actions;

// SELECTORS ===================================================================
const selectResourcesState = state =>
  (state as { resources: typeof initialState })?.resources;

export const { selectAll: selectAllResources } =
  resourcesAdapter.getSelectors(selectResourcesState);

export const selectResourceZipcode = createSelector(
  selectResourcesState,
  state => state.zipcode
);

export const selectResourceTerms = createSelector(
  selectResourcesState,
  state => state.terms
);

export const selectResourceTagKeys = createSelector(
  selectResourcesState,
  state => state.tags
);

export const selectResourcesLoading = createSelector(
  selectResourcesState,
  state => state.loadingResources
);

export const selectSelectedResourceId = createSelector(
  selectResourcesState,
  state => state.selectedResourceId
);

export const selectSelectedResource = createSelector(
  selectResourcesState,
  state => state.selectedResource
);

export const selectSelectedResourceLoading = createSelector(
  selectResourcesState,
  state => state.loadingResource
);

export const selectResourceTags = createSelector(
  selectResourceTagKeys,
  selectAllResourceTags,
  (keys, tags) => {
    const keyMap = (keys || []).reduce(
      (map, key) => {
        map[key] = key;
        return map;
      },
      {} as { [keys: string]: string | ResourceTagDto }
    );

    const findTags = (tags: ResourceTagDto[]) => {
      tags.forEach(tag => {
        if (keyMap[tag.key]) {
          keyMap[tag.key] = tag;
        }
        if (tag.children) {
          findTags(tag.children);
        }
      });
    };
    findTags(tags || []);
    return (keys || []).map(key => keyMap[key]);
  }
);

export const selectResourceSearch = createSelector(
  selectResourcesState,
  state =>
    !state.terms && !state.tags
      ? null
      : { terms: state.terms, tags: state.tags }
);

export const selectClientId = createSelector(
  selectResourcesState,
  state => state.clientId
);

// EXPORT HELPER ===============================================================

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: resourceSelected,
    effect: async ({ dispatch, action, state }) => {
      if (action.payload.resourceId) {
        const zipcode = selectResourceZipcode(state);
        const resourceState = selectResourcesState(state);
        const clientId = selectClientId(state);
        if (!resourceState.entities[action.payload.resourceId]) {
          dispatch(
            getSelectedResource({
              params: {
                clientId,
                resourceId: action.payload.resourceId,
                zipcode,
              },
            })
          );
        }
      }
    },
  });
  addEffect({
    actions: [
      resourceZipcodeUpdated,
      resourceTermsUpdated,
      resourceTagsUpdated,
    ],
    effect: async ({ dispatch, state }) => {
      const zipcode = selectResourceZipcode(state);
      const terms = selectResourceTerms(state);
      const tags = selectResourceTagKeys(state);
      const clientId = selectClientId(state);

      if (zipcode) {
        localStorage.setItem(LOCAL_STORAGE_ZIPCODE_KEY, zipcode);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_ZIPCODE_KEY);
      }

      if (zipcode && (terms || tags)) {
        dispatch(
          getResources({
            params: { clientId, zipcode, terms, serviceTags: tags },
          })
        );
      } else {
        dispatch(clearResources());
      }
    },
  });
  addEffect({
    action: resourceZipcodeUpdated,
    effect: async ({ action }) => {
      const zipcode = action.payload.zipcode;
      if (zipcode) {
        localStorage.setItem(LOCAL_STORAGE_ZIPCODE_KEY, zipcode);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_ZIPCODE_KEY);
      }
    },
  });
});

// WEBSOCKET ACTIONS ===========================================================
